import * as React from 'react';
import styled from 'styled-components';
import { laptopSize, mobileSize } from '../../../util/variables';
import { Base10Logo } from '../../../svg/base10logo';
import theme from '../../../util/theme';
import graduatingStudentsImage from '../../../content/img/GraduatingStudents.webp';

interface SubcontentBlockProps {
  isFirst?: boolean;
}

const PurposeAtBase10: React.FC = () => {
  return (
    <Container>
      <HeroContainer>
        <Hero>
          <HeroContent>
            <Title>
              <TitleLine>Purpose</TitleLine>
              <TitleLine>
                <AtText>at</AtText>
                <LogoWrapper>
                  <Base10Logo color="white" size={1.4} />
                </LogoWrapper>
              </TitleLine>
            </Title>
          </HeroContent>
        </Hero>
      </HeroContainer>
      <ContentWrapper>
        <SubcontentWrapper>
          <SubcontentBlock isFirst>
            <Styledh2>
              We are a venture capital firm that believes{' '}
              <span style={{ color: theme.PRIMARY_COLOR_DARK }}>purpose</span>{' '}
              is key to{' '}
              <span style={{ color: theme.PRIMARY_COLOR_DARK }}>profits</span>.
            </Styledh2>
            <StyledParagraph>
              We invest in mission-aligned founders building companies solving problems for the 99%. We leverage our resources and knowledge to help each of our portfolio companies discover and advance its own principles of purpose.
              We are returns-focused investors who believe that you win when your community wants you to win, and that a thoughtful and authentic impact strategy is good for business.
            </StyledParagraph>
          </SubcontentBlock>
          <SubcontentBlock>
            <Styledh2>
              Our impact activities collectively are called<br />
              <span style={{ color: theme.PRIMARY_COLOR_DARK }}>
                The Advancement Initiative
              </span>.
            </Styledh2>
            <StyledParagraph>
              Base10 and its Managing Partners have committed to donate 50% of the carried interest earned from select Base10 managed funds to organizations focused on expanding access for students with limited opportunities in tech and entrepreneurship. Our goal is for these donations to support students who have ambitions to be founders and tech industry leaders.
            </StyledParagraph>
            <StyledParagraph>
              A portion of the donations will be used to create scholarships for students in the names of the portfolio companies who contribute to fund performance.
            </StyledParagraph>
          </SubcontentBlock>
        </SubcontentWrapper>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.section`
  width: 100%;
  background-color: ${theme.BLACK_COLOR};
`;

const HeroContainer = styled.div`
  width: 100%;
`;

const Hero = styled.div`
  width: 100%;
  background-image: url(${graduatingStudentsImage});
  background-size: cover;
  background-position: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 60px 44px 40px 44px;
  display: flex;
  justify-content: flex-start;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 80px 44px;

  @media ${laptopSize} {
    padding: 40px 44px;
  }
`;

const SubcontentWrapper = styled.div`
  display: flex;
  gap: 120px;

  @media ${laptopSize} {
    flex-direction: column;
    gap: 0px;
  }
`;

const SubcontentBlock = styled.div<SubcontentBlockProps>`
  display: flex;
  flex-direction: column;
  flex: ${props => props.isFirst ? 1.0 : 0.9};
`;

const Title = styled.h2`
  font-size: 100px;
  font-weight: 700;
  margin-bottom: 30px;
  line-height: 1;
  color: ${theme.WHITE_COLOR};
    
  @media ${mobileSize} {
    font-size: 70px;
  }
`;

const TitleLine = styled.div`
  color: ${theme.PRIMARY_COLOR_DARK};
  display: flex;
  align-items: baseline;
  gap: 8px;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
`;

const AtText = styled.span`
  display: inline-block;
  transform: translateY(14px);
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
`;

const LogoWrapper = styled.span`
  display: inline-flex;
  align-items: flex-end;
  margin-left: 50px;
  padding-bottom: 60px;
  scale: 1.4;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  @media ${mobileSize} {
    scale: 1;
    margin-left: 10px;
    padding-bottom: 0px;
  }
`;

const Styledh2 = styled.h2`
  font-size: 32px;
  line-height: 1.2;
  color: ${theme.WHITE_COLOR};
  font-family: ${theme.PRIMARY_FONT};
  font-weight: 700;
  
  @media ${mobileSize} {
    font-size: 24px;
  }
`;

const StyledParagraph = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 1em;
  color: ${theme.WHITE_COLOR};
`;

export default PurposeAtBase10;
